/**
 * InnoCalls WebRTC dialer bridge — web only for now (native support pending
 * confirmed SIP/WebSocket connection details from InnoCalls, see
 * project_innocalls_api memory). Mirrors the reference implementation's
 * InnocallsBridge.ts (window event + sessionStorage queueing pattern) but
 * scoped to this app's phone-dialer hook instead of a page-wide dialer widget.
 */

import { installNoiseSuppressedGetUserMedia } from './noise-suppression.service';

export const PHONE_CALL_STARTED_EVENT = 'phone-dialer:call-started';
export const PHONE_CALL_ANSWERED_EVENT = 'phone-dialer:call-answered';
export const PHONE_CALL_ENDED_EVENT = 'phone-dialer:call-ended';

// Committed directly to InnoCalls' own SDK outcome signals — no heuristic
// "was it answered" guessing. onCallEnded/onCallMissed/onCallRejected are
// the SDK's real outcomes; 'userHangup' is OUR app's own signal, fired when
// the agent presses "End Call" before any of the three SDK outcomes arrived.
export type PhoneDialerEndReason = 'callEnded' | 'callMissed' | 'callRejected' | 'callFailed' | 'userHangup';

export interface PhoneCallEndedDetail {
  reason: PhoneDialerEndReason;
  payload?: unknown;
}

export type PhoneDialerCallStateLabel = 'idle' | 'calling' | 'ringing' | 'connected' | 'completed' | 'not-responded' | 'cancelled' | 'ended' | 'failed';

export const getCallStatusLabel = (state: PhoneDialerCallStateLabel): string => {
  switch (state) {
    case 'calling': return 'Calling…';
    case 'ringing': return 'Ringing…';
    case 'connected': return 'Connected';
    case 'completed': return 'Call completed';
    case 'cancelled': return 'Busy';
    case 'not-responded': return 'No answer';
    case 'failed': return 'Call failed';
    case 'ended': return 'Call ended';
    default: return '';
  }
};

export const getCallStatusColor = (state: PhoneDialerCallStateLabel): string => {
  switch (state) {
    case 'connected':
    case 'completed':
      return '#22C55E';
    case 'cancelled':
    case 'not-responded':
    case 'failed':
      return '#EF4444';
    case 'ended': return '#9CA3AF';
    default: return '#F59E0B'; // calling / ringing
  }
};

/**
 * Produces clean E.164 (+<countrycode><number>, digits only after the
 * leading +) — no single-country assumption, since Sendifier is a
 * multi-tenant CRM whose contacts can be any country. The original
 * reference implementation this dialer was adapted from assumed Saudi
 * (+966) local numbers only (stripping "966" and forcing a leading "0"),
 * which silently produced invalid numbers for non-Saudi contacts (e.g. an
 * Egyptian +20 number came out as "0201023356631" — neither a valid Saudi
 * nor Egyptian number) and caused InnoCalls to accept startCall() without
 * ever placing a real call. If the input has no country code, it's passed
 * through as-is (best effort) rather than guessing one.
 *
 * NOTE: this is the fallback used when the business's own caller-number
 * country can't be determined — see formatDialTarget below for the actual
 * dial-time formatting, which is country-aware and differs from this.
 */
export const normalizePhoneNumber = (value: string): string => {
  const digits = value.trim().replace(/\D/g, '');
  if (!digits) return '';
  return `+${digits}`;
};

/**
 * Country-aware dial-target formatting — distinct from (and more specific
 * than) normalizePhoneNumber above, which always produces plain E.164.
 *
 * Confirmed with InnoCalls' own account behavior: a call to a destination in
 * the SAME country as the business's own configured InnoCalls number must be
 * sent in local/national format (leading "0" + national number, no country
 * code) — sending it as full E.164 (+<countrycode><number>) for a same-
 * country destination causes InnoCalls to silently accept startCall() and
 * never place the call (no SIP response of any kind, unlike a real busy/
 * reject signal). Cross-country calls must still use full E.164.
 *
 * This is NOT the same bug the old normalizePhoneNumber doc comment
 * describes (that was a blanket "always assume Saudi" rule applied to every
 * number regardless of destination, which broke non-Saudi contacts) — this
 * rule only rewrites the destination when it's confirmed to match the
 * business's own country, determined dynamically per-business via
 * businessCallerNumber (the connected InnoCalls account's own number),
 * never hardcoded to any one country.
 *
 * e.g. business caller number +966... :
 *   966538380401 (Saudi destination) -> 0538380401   (local format)
 *   201023356631 (Egypt destination) -> +201023356631 (unchanged, cross-country)
 */
export const formatDialTarget = (rawNumber: string, businessCallerNumber?: string): string => {
  const e164 = normalizePhoneNumber(rawNumber);
  if (!e164 || !businessCallerNumber) return e164;

  try {
    // Lazy import keeps this file's only hard dependency (libphonenumber-js)
    // scoped to where it's actually used, matching src/utils/phoneNumber.ts's
    // existing usage pattern elsewhere in this app.
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { parseInternationalPhoneNumber } = require('../utils/phoneNumber');
    const destination = parseInternationalPhoneNumber(e164);
    const business = parseInternationalPhoneNumber(businessCallerNumber);

    if (!destination?.country || !business?.country) return e164;
    if (destination.country !== business.country) return e164; // cross-country — keep E.164

    // Same country as the business's own number — local format: leading
    // "0" + national significant number, no country code.
    return `0${destination.nationalNumber}`;
  } catch (error) {
    console.warn('[phone-dialer.service] formatDialTarget: falling back to E.164, country detection failed:', error);
    return e164;
  }
};

export interface InnoCallsRTCOptions {
  apiKey: string;
  extension: string;
  webrtcSecret: string;
  config?: { baseColor?: string };
  terminateOnRefresh?: boolean;
}

type InnoCallsEventName =
  | 'callRinging'
  | 'callStarted'
  | 'callMissed'
  | 'callRejected'
  | 'callEnded'
  | 'held'
  | 'released'
  | 'muted'
  | 'unmuted'
  | 'callAnswered'
  | 'callFailed';

/**
 * Confirmed via console.dir() of a real constructed instance (2026-07-16) —
 * the reference implementation's assumed .on()/.off()/mount()/destroy() event
 * API does NOT exist on the actual shipped SDK. The real API is direct
 * property assignment of onCallX callbacks (pre-existing no-op properties on
 * the instance, not an event emitter) and there is no separate mount step —
 * the widget presumably auto-mounts into the DOM on construction or on
 * showWebrtc(). No onCallAnswered property exists; "answered" must be
 * inferred from onCallStarted's payload or is simply not distinguished from
 * "started" by this SDK.
 */
export interface InnoCallsRTCInstance {
  mount?: (selector: string) => void;
  unmount?: () => void;
  destroy?: () => void;
  on?: (event: InnoCallsEventName, handler: (payload?: unknown) => void) => void;
  off?: (event: InnoCallsEventName, handler: (payload?: unknown) => void) => void;
  showWebrtc?: () => void;
  hideWebrtc?: () => void;
  toggleWebrtc?: () => void;
  startCall?: (number: string) => void;
  hangup?: () => void;
  fillNumber?: (number: string) => void;
  onCallRinging?: (payload?: unknown) => void;
  onCallStarted?: (payload?: unknown) => void;
  onCallMissed?: (payload?: unknown) => void;
  onCallRejected?: (payload?: unknown) => void;
  onCallEnded?: (payload?: unknown) => void;
}

type InnoCallsRTCConstructor = new (options: InnoCallsRTCOptions) => InnoCallsRTCInstance;

declare global {
  interface Window {
    InnocallsRTC?: InnoCallsRTCConstructor;
  }
}

const INNOCALLS_SCRIPT_URL = 'https://innocalls-statics.s3.ap-south-1.amazonaws.com/webrtc/webrtc.js';
const INNOCALLS_CSS_URL = 'https://innocalls-statics.s3.ap-south-1.amazonaws.com/webrtc/style.css';
const INNOCALLS_SCRIPT_ID = 'innocalls-script';
const INNOCALLS_CSS_ID = 'innocalls-style';
const LIBRARY_WAIT_TIMEOUT_MS = 10_000;
const LIBRARY_POLL_INTERVAL_MS = 200;

let innocallsScriptLoadPromise: Promise<void> | null = null;

/**
 * The InnoCalls SDK has no reliable "call answered" callback on this build
 * (see usePhoneDialer's doc comments — onCallAnswered doesn't exist, and the
 * speculative .on('callAnswered', ...) listener has never been observed to
 * fire). Rather than guessing with a fixed timeout, patch the GLOBAL
 * RTCPeerConnection constructor once, before the SDK script loads, so every
 * peer connection the SDK creates internally — regardless of what the SDK
 * itself exposes — still gets our own connectionState listener attached.
 * WebRTC audio cannot flow before the callee actually answers, so a
 * connection reaching 'connected' is real ground truth, not a heuristic.
 * Each call to startCall() should call getNextPeerConnectionConnected() to
 * get a promise for the NEXT peer connection this patch observes reaching
 * 'connected' — safe to let multiple calls overlap since each call gets its
 * own promise tied to a specific connection instance.
 */
type PeerConnectedListener = (pc: RTCPeerConnection) => void;
const activePeerConnections = new Set<RTCPeerConnection>();
let callMicrophoneMuted = false;
export function setCallMicrophoneMuted(muted: boolean): void {
  callMicrophoneMuted = muted;
  activePeerConnections.forEach(pc => pc.getSenders().forEach(sender => {
    if (sender.track?.kind === 'audio') sender.track.enabled = !muted;
  }));
}
const peerConnectedListeners = new Set<PeerConnectedListener>();
let rtcPeerConnectionPatched = false;

// TEMPORARY DIAGNOSTIC AID — investigating a confirmed bug where calls placed
// from inside the native app's WebView lose incoming audio (or the whole call
// tears down) around 10-12s in, while the same InnoCalls account/number works
// fine from a normal browser tab. Rather than guess again, this reports the
// underlying RTCPeerConnection's real ICE/connection state transitions plus
// periodic inbound-audio packet counts so the next real test call shows
// exactly what happens at that mark (an ICE disconnect? DTLS restart? packets
// still arriving but not being played?) instead of another blind fix attempt.
// Remove once the root cause is found and fixed.
export interface PeerConnectionDiagnosticEvent {
  atMs: number;
  message: string;
}
type DiagnosticListener = (event: PeerConnectionDiagnosticEvent) => void;
const diagnosticListeners = new Set<DiagnosticListener>();
export const subscribePeerConnectionDiagnostics = (listener: DiagnosticListener): (() => void) => {
  diagnosticListeners.add(listener);
  return () => diagnosticListeners.delete(listener);
};
const emitDiagnostic = (message: string) => {
  const event = { atMs: Date.now(), message };
  diagnosticListeners.forEach((listener) => listener(event));
};

const attachDiagnostics = (pc: RTCPeerConnection) => {
  const startedAt = Date.now();
  const elapsed = () => `${((Date.now() - startedAt) / 1000).toFixed(1)}s`;
  pc.addEventListener('iceconnectionstatechange', () => emitDiagnostic(`[${elapsed()}] iceConnectionState=${pc.iceConnectionState}`));
  pc.addEventListener('connectionstatechange', () => emitDiagnostic(`[${elapsed()}] connectionState=${pc.connectionState}`));
  pc.addEventListener('signalingstatechange', () => emitDiagnostic(`[${elapsed()}] signalingState=${pc.signalingState}`));
  pc.addEventListener('icegatheringstatechange', () => emitDiagnostic(`[${elapsed()}] iceGatheringState=${pc.iceGatheringState}`));

  let lastPacketsReceived: number | null = null;
  let lastBytesReceived: number | null = null;
  const pollStats = async () => {
    if (pc.connectionState === 'closed') return;
    try {
      const stats = await pc.getStats();
      stats.forEach((report) => {
        if (report.type === 'inbound-rtp' && report.kind === 'audio') {
          const packets = report.packetsReceived ?? 0;
          const bytes = report.bytesReceived ?? 0;
          const deltaPackets = lastPacketsReceived == null ? null : packets - lastPacketsReceived;
          const deltaBytes = lastBytesReceived == null ? null : bytes - lastBytesReceived;
          emitDiagnostic(
            `[${elapsed()}] inbound-audio packets=${packets}(+${deltaPackets ?? '?'}) bytes=${bytes}(+${deltaBytes ?? '?'}) jitter=${report.jitter ?? '?'} packetsLost=${report.packetsLost ?? '?'}`
          );
          lastPacketsReceived = packets;
          lastBytesReceived = bytes;
        }
      });
    } catch (err) {
      emitDiagnostic(`[${elapsed()}] getStats() failed: ${err}`);
    }
  };
  const statsTimer = setInterval(pollStats, 2000);
  pc.addEventListener('connectionstatechange', () => {
    if (pc.connectionState === 'closed') clearInterval(statsTimer);
  });
};

const patchRTCPeerConnectionForAnsweredDetection = () => {
  if (rtcPeerConnectionPatched) return;
  if (typeof window === 'undefined' || typeof window.RTCPeerConnection !== 'function') return;
  rtcPeerConnectionPatched = true;

  const NativeRTCPeerConnection = window.RTCPeerConnection;

  const PatchedRTCPeerConnection = function (this: RTCPeerConnection, ...args: any[]) {
    // @ts-ignore — constructing via apply to forward arbitrary constructor args
    const pc: RTCPeerConnection = new NativeRTCPeerConnection(...args);
    activePeerConnections.add(pc);
    pc.addEventListener('signalingstatechange', () => {
      if (pc.signalingState === 'closed') activePeerConnections.delete(pc);
      else setCallMicrophoneMuted(callMicrophoneMuted);
    });
    let notified = false;
    const notifyIfConnected = () => {
      if (notified) return;
      if (pc.connectionState === 'connected' || pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
        notified = true;
        peerConnectedListeners.forEach((listener) => listener(pc));
      }
    };
    pc.addEventListener('connectionstatechange', notifyIfConnected);
    pc.addEventListener('iceconnectionstatechange', notifyIfConnected);
    pc.addEventListener('track', notifyIfConnected);
    attachDiagnostics(pc);
    return pc;
  } as unknown as typeof RTCPeerConnection;

  PatchedRTCPeerConnection.prototype = NativeRTCPeerConnection.prototype;
  window.RTCPeerConnection = PatchedRTCPeerConnection;
  console.log('[PhoneDialer] RTCPeerConnection patched for answered-call detection');
};

/** Resolves the first time ANY peer connection created after this call reaches
 * 'connected' — call once per outbound call, right before/around startCall(). */
const getNextPeerConnectionConnectedSignal = (timeoutMs: number): Promise<boolean> =>
  new Promise((resolve) => {
    patchRTCPeerConnectionForAnsweredDetection();
    let settled = false;
    const listener: PeerConnectedListener = () => {
      if (settled) return;
      settled = true;
      peerConnectedListeners.delete(listener);
      resolve(true);
    };
    peerConnectedListeners.add(listener);
    setTimeout(() => {
      if (settled) return;
      settled = true;
      peerConnectedListeners.delete(listener);
      resolve(false);
    }, timeoutMs);
  });

/** Call once per outbound call, right before/around startCall(). Resolves
 * true the moment a peer connection this session created reaches
 * 'connected', or false if none does within timeoutMs. Logs loudly if the
 * patch never even applied (e.g. no window.RTCPeerConnection in this
 * environment) so a failure mode is visible without digging through the
 * console — this detector's reliability depends on the InnoCalls SDK
 * actually calling the GLOBAL window.RTCPeerConnection at runtime, which is
 * unconfirmed for this SDK build; if it turns out NOT to (a bundled/
 * minified script can carry its own captured reference instead), this will
 * consistently resolve false and armRingingTimeout's fixed-delay fallback
 * takes over exactly as it did before this detector existed — never worse
 * than the prior behavior, only better when the patch does apply. */
export const getNextPeerConnectionConnected = (timeoutMs: number): Promise<boolean> => {
  if (typeof window === 'undefined' || typeof window.RTCPeerConnection !== 'function') {
    console.warn('[PhoneDialer] window.RTCPeerConnection unavailable in this environment — answered-detection patch cannot apply, falling back to the fixed timeout only');
  }
  return getNextPeerConnectionConnectedSignal(timeoutMs);
};

/**
 * The call's real SIP signalling, read from the SDK's own WebSocket (SIP over WebSocket carries
 * plain-text messages). This SDK build has no "answered" event, so the dialer used to guess
 * (2nd ringing event, or 45 s of ringing = answered) — long-ringing calls then showed a running
 * counter and ended as "completed". SIP responses are the ground truth:
 *   INVITE 180/183 → ringing · 200 → answered · 486/600/603 → busy/declined ·
 *   408/480/487 → no answer / cancelled · other 4xx–6xx → failed · incoming BYE → hung up.
 * Installed before the SDK script loads (like the RTCPeerConnection patch above); when the SDK
 * uses another transport nothing is ever emitted and the dialer keeps its previous behaviour.
 */
export type SipSignal = { kind: 'response'; status: number; method: string } | { kind: 'request'; method: string };
type SipListener = (signal: SipSignal) => void;
const sipListeners = new Set<SipListener>();
export const subscribeSipSignals = (listener: SipListener): (() => void) => {
  sipListeners.add(listener);
  return () => sipListeners.delete(listener);
};
/** One SIP message (text) → the signal it carries; anything else → null. */
export function parseSipMessage(raw: unknown, direction: 'in' | 'out'): SipSignal | null {
  if (typeof raw !== 'string') return null;
  const head = raw.slice(0, 2000);
  const status = /^SIP\/2\.0 (\d{3})/.exec(head);
  const method = /^([A-Z]+) \S+ SIP\/2\.0/.exec(head);
  if (status) {
    const cseq = /\r?\n(?:CSeq|l)\s*:\s*\d+\s+([A-Z]+)/i.exec(head);
    return direction === 'in' && cseq ? { kind: 'response', status: Number(status[1]), method: cseq[1].toUpperCase() } : null;
  }
  return method ? { kind: 'request', method: method[1] } : null;
}
let webSocketPatched = false;
const patchWebSocketForSipSignals = () => {
  if (webSocketPatched || typeof window === 'undefined' || typeof window.WebSocket !== 'function') return;
  webSocketPatched = true;
  const NativeWebSocket = window.WebSocket;
  class SipObservingWebSocket extends NativeWebSocket {
    constructor(url: string | URL, protocols?: string | string[]) {
      super(url, protocols as any);
      this.addEventListener('message', (event: MessageEvent) => {
        const signal = parseSipMessage(event.data, 'in');
        if (signal) sipListeners.forEach((listener) => listener(signal));
      });
    }
  }
  window.WebSocket = SipObservingWebSocket as unknown as typeof WebSocket;
};

export const ensureInnocallsStylesheet = () => {
  if (document.getElementById(INNOCALLS_CSS_ID)) return;
  const link = document.createElement('link');
  link.id = INNOCALLS_CSS_ID;
  link.rel = 'stylesheet';
  link.href = INNOCALLS_CSS_URL;
  document.head.appendChild(link);
};

export const ensureInnocallsScript = (): Promise<void> => {
  // Both patches MUST happen before the SDK script loads — patching after
  // the fact would miss whatever global references the SDK module captures
  // at import time. Two independent patches, two independent concerns
  // (RTCPeerConnection answered-detection, currently unused but left
  // installed; getUserMedia noise suppression) — kept as separate calls so
  // each can be reasoned about, tested, and disabled independently.
  patchRTCPeerConnectionForAnsweredDetection();
  patchWebSocketForSipSignals();
  installNoiseSuppressedGetUserMedia();
  if (window.InnocallsRTC) return Promise.resolve();
  if (innocallsScriptLoadPromise) return innocallsScriptLoadPromise;

  innocallsScriptLoadPromise = new Promise<void>((resolve, reject) => {
    const existingScript = document.getElementById(INNOCALLS_SCRIPT_ID) as HTMLScriptElement | null;
    const handleLoad = () => resolve();
    const handleError = () => reject(new Error('Failed to load InnoCalls script'));

    if (existingScript) {
      if (existingScript.dataset.loaded === 'true' || window.InnocallsRTC) {
        resolve();
        return;
      }
      existingScript.addEventListener('load', handleLoad, { once: true });
      existingScript.addEventListener('error', handleError, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = INNOCALLS_SCRIPT_ID;
    script.src = INNOCALLS_SCRIPT_URL;
    script.type = 'module';
    script.async = true;
    script.addEventListener('load', () => {
      script.dataset.loaded = 'true';
      handleLoad();
    });
    script.addEventListener('error', handleError, { once: true });
    document.body.appendChild(script);
  }).catch((error) => {
    innocallsScriptLoadPromise = null;
    throw error;
  });

  return innocallsScriptLoadPromise;
};

export const waitForInnocallsRTC = (): Promise<InnoCallsRTCConstructor> =>
  new Promise((resolve, reject) => {
    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      if (window.InnocallsRTC) {
        window.clearInterval(timer);
        resolve(window.InnocallsRTC);
        return;
      }
      if (Date.now() - startedAt > LIBRARY_WAIT_TIMEOUT_MS) {
        window.clearInterval(timer);
        reject(new Error('InnoCalls script loaded but window.InnocallsRTC is unavailable'));
      }
    }, LIBRARY_POLL_INTERVAL_MS);
  });

export const destroyInnoCallsInstance = (instance: InnoCallsRTCInstance | null, rootId: string) => {
  if (!instance) return;
  try {
    if (typeof instance.destroy === 'function') instance.destroy();
    else if (typeof instance.unmount === 'function') instance.unmount();
  } catch (error) {
    console.error('[PhoneDialer] Failed to clean up InnoCalls instance', error);
  } finally {
    document.getElementById(rootId)?.replaceChildren();
  }
};
